import React from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { KpiStrip } from '../../components/KpiStrip';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { NegoceFilterBar } from './NegoceFilterBar';
import { useNegoceFilters } from './NegoceFilters';
import { usePresentationKpis } from 'apiTyped/hooks/negoce.query';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number | undefined | null): string =>
  n != null ? n.toLocaleString('fr-FR') : '—';

const formatPercent = (n: number | undefined | null): string =>
  n != null ? `${n.toFixed(1)}%` : '—';

// ─── Component ──────────────────────────────────────────────────────────────

export const NegocePresentation: React.FC = () => {
  const { campaignId, wineId, negociantId, negociants } = useNegoceFilters();

  const { isLoadingPresentationKpis, presentationKpis } = usePresentationKpis({
    campaignId,
    negociantId,
    wineId
  });

  const kpis = presentationKpis as any;
  const selectedNegociant = negociants.find((n: any) => n.id === negociantId);

  const kpiItems = [
    {
      label: 'Bottles sold (eq. 75cl)',
      value: isLoadingPresentationKpis
        ? '...'
        : formatNumber(kpis?.bottles_sold)
    },
    {
      label: 'Number of customers',
      value: isLoadingPresentationKpis
        ? '...'
        : formatNumber(kpis?.customers_count)
    },
    {
      label: 'Exclusivity rate',
      value: isLoadingPresentationKpis
        ? '...'
        : formatPercent(kpis?.exclusivity_rate)
    },
    {
      label: 'Resale rate',
      value: isLoadingPresentationKpis
        ? '...'
        : formatPercent(kpis?.resale_rate)
    },
    {
      label: 'Loyalty rate',
      value: isLoadingPresentationKpis
        ? '...'
        : formatPercent(kpis?.loyalty_rate)
    },
    {
      label: 'Bottles sold N-1',
      value: isLoadingPresentationKpis
        ? '...'
        : formatNumber(kpis?.bottles_sold_n1)
    }
  ];

  return (
    <Page>
      <PageHeader
        title="Presentation"
        subtitle="Detailed KPIs for a specific négociant. Select a campaign and négociant from the filters below."
      />

      <NegoceFilterBar showNegociant />

      {!negociantId ? (
        <EmptyState message="Select a négociant using the filter above to view their performance." />
      ) : (
        <>
          <NegociantBanner>
            <BannerIcon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </BannerIcon>
            <BannerText>
              <BannerName>
                {selectedNegociant?.name || `Négociant #${negociantId}`}
              </BannerName>
              <BannerMeta>Individual performance overview</BannerMeta>
            </BannerText>
          </NegociantBanner>

          <KpiStrip items={kpiItems} />

          {/* Additional per-negociant data could be added here (charts, detail breakdown) */}
        </>
      )}
    </Page>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1200px;
`;

const NegociantBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 16px 20px;
  margin-bottom: 24px;
`;

const BannerIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--paper);
  display: grid;
  place-items: center;
  color: var(--ink-faint);
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BannerText = styled.div``;

const BannerName = styled.div`
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
`;

const BannerMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  margin-top: 2px;
`;
